import * as XLSX from "xlsx";
import { formatDateDMY } from "../../utils/functions";

const hasValue = (v) => v !== null && v !== undefined && v !== "";
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "");
const date = (v) => (hasValue(v) ? formatDateDMY(v) : "");
const number = (v) => (hasValue(v) ? Number(v) : "");
const millionsOf = (v) => (hasValue(v) ? Number((Number(v) / 1000000).toFixed(6)) : "");
const difference = (amount, released) =>
  hasValue(released) ? Number(Math.max(0, Number(amount || 0) - Number(released)).toFixed(6)) : "";
const otherText = (value, other) => (value === "Other/Specify" ? `Other - ${other || ""}` : value || "");
const sdgText = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const districtName = (grant, districts = []) =>
  grant.field_verification_district?.district_name ||
  districts.find((d) => d.district_id === grant.field_verification_district_id)?.district_name ||
  "";

const writeSheet = (rows, sheetName, fileBase) => {
  const headers = rows.reduce((all, row) => {
    Object.keys(row).forEach((h) => !all.includes(h) && all.push(h));
    return all;
  }, []);
  const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
  ws["!cols"] = headers.map((h) => ({
    wch: Math.min(45, Math.max(h.length + 2, ...rows.map((r) => String(r[h] ?? "").length + 2))),
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  const today = new Date().toISOString().split("T")[0];
  XLSX.writeFile(wb, `${fileBase}-${today}.xlsx`);
};

const administrative = (g, ucName) => ({
  Cycle: g.cycle?.cycle_name || "",
  District: g.district?.district_name || "",
  Tehsil: g.tehsil?.tehsil_name || "",
  UC: ucName,
  "Village Name": g.village_name || "",
  Latitude: number(g.latitude),
  Longitude: number(g.longitude),
});

const verificationAndProgress = (g, districts) => ({
  "Field Officer Name": g.field_officer_name || "",
  "Date of Field Verification": date(g.field_verification_date),
  "District of Field Verification": districtName(g, districts),
  "Technical Assistance Provided": yesNo(g.technical_assistance_provided),
  "Verification Approved": yesNo(g.verification_approved),
  "Remarks by Field Officer": g.field_officer_remarks || "",
  "Status of Overall Grant": g.overall_status || "",
  "Date of Completion": date(g.completion_date),
  "Extended Expiry Date": date(g.extended_expiry_date),
  "Remarks (Overall)": g.overall_remarks || "",
  "Status of Overall Grant (First Entry)": g.initial_overall_status || "",
  "Extended Expiry Date (First Entry)": date(g.initial_extended_expiry_date),
  "Remarks (First Entry)": g.initial_overall_remarks || "",
});

export const exportSmeFamilyGrants = (grants, { label, fileBase, isSedf, districts }) => {
  const realMilestones = (g) => (g.milestones || []).filter((m) => Number(m.milestone_number) !== 0);
  const maxMilestones = Math.max(0, ...grants.map((g) => Math.max(0, ...realMilestones(g).map((m) => Number(m.milestone_number)))));

  const rows = grants.map((g) => {
    const byNumber = new Map((g.milestones || []).map((m) => [Number(m.milestone_number), m]));
    const advance = byNumber.get(0);
    const milestoneColumns = {};
    for (let n = 1; n <= maxMilestones; n++) {
      const m = byNumber.get(n) || {};
      const p = `Milestone ${n}`;
      Object.assign(milestoneColumns, {
        [`${p} Title (Activities)`]: m.milestone_title || "",
        [`${p} Documents Required`]: m.documents_required || "",
        [`${p} Amount PKR (M)`]: number(m.amount_pkr),
        [`${p} Amount Euro (M)`]: number(m.amount_euro),
        [`${p} Amount Released PKR (M)`]: number(m.amount_released_pkr),
        [`${p} Difference PKR (M)`]: difference(m.amount_pkr, m.amount_released_pkr),
        [`${p} Amount Released Euro (M)`]: number(m.amount_released_euro),
        [`${p} Difference Euro (M)`]: difference(m.amount_euro, m.amount_released_euro),
        [`${p} Date/Deadline`]: date(m.deadline_date),
        [`${p} Status/Progress`]: m.status || "",
        [`${p} Remarks`]: m.remarks || "",
      });
    }

    return {
      "Application ID": g.application_id || "",
      "Company Name": g.company_name || "",
      ...administrative(g, g.uc_name || ""),
      "Full Name of Applicant": g.applicant_full_name || "",
      "Father/Husband Name": g.father_husband_name || "",
      "CNIC Number": g.cnic_number || "",
      Gender: g.gender || "",
      "Date of Birth": date(g.date_of_birth),
      "Person with Disability": yesNo(g.person_with_disability),
      "Marital Status": g.marital_status || "",
      "Contact Number": g.contact_number || "",
      Email: g.email || "",
      "Business Category": g.business_category || "",
      "Business Sector": otherText(g.business_sector, g.business_sector_other),
      "Business Sub Type": g.business_sub_type || "",
      "SDG Interventions": sdgText(g.sdg_interventions),
      Address: g.address || "",
      "Business Description": g.business_description || "",
      "Date of Selection": date(g.date_of_selection),
      "Project Cost PKR (Amount)": number(g.project_cost_pkr),
      "Project Cost PKR (Million)": millionsOf(g.project_cost_pkr),
      "UNIDO Grant Share PKR (Amount)": number(g.unido_grant_share_pkr),
      "UNIDO Grant Share PKR (Million)": millionsOf(g.unido_grant_share_pkr),
      ...(isSedf
        ? {
            "SEDF Share PKR (Amount)": number(g.sedf_share_pkr),
            "SEDF Share PKR (Million)": millionsOf(g.sedf_share_pkr),
          }
        : {}),
      "Applicant Share PKR (Amount)": number(g.applicant_share_pkr),
      "Applicant Share PKR (Million)": millionsOf(g.applicant_share_pkr),
      "Project Cost Euro (Amount)": number(g.project_cost_euro),
      "Project Cost Euro (Million)": millionsOf(g.project_cost_euro),
      "UNIDO Grant Share Euro (Amount)": number(g.unido_grant_share_euro),
      "UNIDO Grant Share Euro (Million)": millionsOf(g.unido_grant_share_euro),
      ...(isSedf
        ? {
            "SEDF Share Euro (Amount)": number(g.sedf_share_euro),
            "SEDF Share Euro (Million)": millionsOf(g.sedf_share_euro),
          }
        : {}),
      "Applicant Share Euro (Amount)": number(g.applicant_share_euro),
      "Applicant Share Euro (Million)": millionsOf(g.applicant_share_euro),
      "Business Entity Type": otherText(g.business_entity, g.business_entity_other),
      "Date of Evaluation/Qualification Review": date(g.evaluation_date),
      "Combined Score (%)": number(g.combined_score_percentage),
      "Grant Agreement #": g.grant_agreement_number || "",
      "Effective Date (Signed)": date(g.effective_date),
      "Expiry Date": date(g.expiry_date),
      "Approved Grant Amount (PKR) (Million)": number(g.approved_grant_amount_pkr),
      "Approved Grant Amount (EURO) (Million)": number(g.approved_grant_amount_euro),
      "Minutes of Qualification Uploaded": g.qualification_minutes_uploaded ? "Yes" : "No",
      "Number of Milestones": realMilestones(g).length || "",
      "Include 7% Advance": g.include_seven_percent_advance ? "Yes" : "No",
      "7% Advance Adjusted In":
        g.include_seven_percent_advance && g.seven_percent_adjust_milestone
          ? `Milestone ${g.seven_percent_adjust_milestone}`
          : "",
      "7% Advance Amount PKR (M)": number(advance?.amount_pkr),
      "7% Advance Amount Euro (M)": number(advance?.amount_euro),
      "7% Advance Amount Released PKR (M)": number(advance?.amount_released_pkr),
      "7% Advance Difference PKR (M)": advance ? difference(advance.amount_pkr, advance.amount_released_pkr) : "",
      "7% Advance Amount Released Euro (M)": number(advance?.amount_released_euro),
      "7% Advance Difference Euro (M)": advance ? difference(advance.amount_euro, advance.amount_released_euro) : "",
      "7% Advance Status": advance?.status || "",
      ...milestoneColumns,
      ...verificationAndProgress(g, districts),
    };
  });

  writeSheet(rows, label.slice(0, 31), fileBase);
};

export const exportMicroGrants = (grants, { fileBase, districts }) => {
  const rows = grants.map((g) => ({
    "Application ID": g.application_id || "",
    "Company Name": g.company_name || "",
    ...administrative(g, g.uc?.uc_name || g.uc_name || ""),
    "Full Name of Applicant": g.applicant_full_name || "",
    "Father/Husband Name": g.father_husband_name || "",
    "CNIC Number": g.cnic_number || "",
    Gender: g.gender || "",
    "Date of Birth": date(g.date_of_birth),
    "Person with Disability": yesNo(g.person_with_disability),
    "Marital Status": g.marital_status || "",
    "Contact Number": g.contact_number || "",
    Email: g.email || "",
    Address: g.address || "",
    "Business Category": g.business_category || "",
    "Business Entity Type": otherText(g.business_entity, g.business_entity_other),
    "Business Sector": otherText(g.business_sector, g.business_sector_other),
    "SDG Interventions": sdgText(g.sdg_interventions),
    "Business Sub Type": g.business_sub_type || "",
    "Grant Asked Amount (PKR) (Million)": number(g.grant_asked_amount),
    "Cost Rationalization Amount (PKR) (Million)": number(g.cost_rationalization_amount),
    "Item Details / Equipment/Material Provided": g.item_details || "",
    "ECIB Clearance": yesNo(g.ecib_clearance),
    "Date of Registration for Grant": date(g.registration_date),
    "Date of Application Submission": date(g.application_submission_date),
    "Date of Selection": date(g.selection_date),
    "Date of Approval": date(g.approval_date),
    "Date of Disbursement": date(g.disbursement_date),
    ...verificationAndProgress(g, districts),
  }));

  writeSheet(rows, "Micro Grants", fileBase);
};
